import DOMPurify from 'dompurify';

// Force rel="noopener noreferrer" on any link that opens a new tab, so a
// target="_blank" link in an EXTERNAL email body can't reverse-tabnab the CRM
// via window.opener. Registered once at module load (DOMPurify hooks are global).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Make every link open in a new tab AND attach rel=noopener noreferrer, so a
  // link in an EXTERNAL email body opens away from the CRM and cannot
  // reverse-tabnab it via window.opener. Registered once (hooks are global).
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  // Inline styles are allowed (for email fidelity) but strip the declarations
  // that could let external mail hijack the page layout — fixed/absolute/sticky
  // positioning, z-index overlays, and CSS injection vectors.
  const el = node as Element;
  const style = el.getAttribute?.('style');
  if (style) {
    const cleaned = style
      .split(';')
      .filter((decl) => {
        const [propRaw, valRaw = ''] = decl.split(':');
        const prop = propRaw.trim().toLowerCase();
        const val = valRaw.trim().toLowerCase();
        if (prop === 'position' && /fixed|absolute|sticky/.test(val)) return false;
        if (prop === 'z-index') return false;
        if (/@import|expression\s*\(|javascript:/.test(decl.toLowerCase())) return false;
        return true;
      })
      .join(';');
    if (cleaned !== style) el.setAttribute('style', cleaned);
  }
});

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this function whenever rendering user-generated HTML content
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'strike', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img', 'video',
      'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span',
      'hr'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'width', 'height',
      'class', 'style',
      'controls', 'autoplay', 'muted', 'loop', 'preload', 'poster',
      'data-*'
    ],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target'],
    FORCE_BODY: true,
  });
};

/**
 * Sanitize HTML and return as object for dangerouslySetInnerHTML
 */
export const createSanitizedMarkup = (html: string | null | undefined): { __html: string } => {
  return { __html: sanitizeHtml(html) };
};

/**
 * Stricter sanitizer for EXTERNAL email bodies. Same as sanitizeHtml but
 * forbids the `style` attribute, so a malicious inbound email cannot inject a
 * full-page CSS overlay (e.g. position:fixed) into the CRM. Use for rendering
 * received email HTML; the looser sanitizeHtml stays for trusted internal HTML.
 */
export const sanitizeEmailHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'strike', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'hr',
    ],
    // Allow inline `style` so emails keep their original look (logos, colours,
    // alignment). The afterSanitizeAttributes hook below strips the dangerous
    // positioning declarations so a mail can't lay a full-page overlay over the
    // CRM. (User chose fidelity over the stricter no-style policy.)
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style', 'align', 'bgcolor', 'color'],
    // Explicit URI allowlist (defense-in-depth over DOMPurify's default
    // javascript:/data: stripping) — only safe link/image protocols.
    ALLOWED_URI_REGEXP: /^(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid):/i,
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORCE_BODY: true,
  });
};

/** sanitizeEmailHtml as a dangerouslySetInnerHTML-ready object. */
export const createSanitizedEmailMarkup = (html: string | null | undefined): { __html: string } => {
  return { __html: sanitizeEmailHtml(html) };
};

export default sanitizeHtml;
